import { Input } from '@/components/ui/input'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { FileSpreadsheet, Loader2, Upload } from 'lucide-react'
import { useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Button } from 'vnetwork-ui'
import { compareAccountingRevenue } from '../../../services'

interface CompareAccountingDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    /** Pre-filled months from current filters (YYYY-MM) */
    defaultMonths?: string[]
}

export function CompareAccountingDialog({
    open,
    onOpenChange,
    defaultMonths,
}: CompareAccountingDialogProps) {
    const { t } = useTranslation('report')
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [reportMonth, setReportMonth] = useState(
        () => defaultMonths?.[0] || ''
    )
    const [file, setFile] = useState<File | null>(null)
    const [isComparing, setIsComparing] = useState(false)

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files?.[0]
        if (selected) {
            if (!selected.name.endsWith('.xlsx')) {
                toast.error(t('compare_accounting.invalid_file'))
                return
            }
            setFile(selected)
        }
    }

    const handleSubmit = async () => {
        if (!reportMonth || !file) return

        try {
            setIsComparing(true)
            await compareAccountingRevenue(reportMonth, file)
            toast.success(t('compare_accounting.success'))
            onOpenChange(false)
        } catch {
            toast.error(t('compare_accounting.error'))
        } finally {
            setIsComparing(false)
        }
    }

    const handleClose = (value: boolean) => {
        if (!isComparing) {
            onOpenChange(value)
            if (!value) {
                setFile(null)
                setReportMonth(defaultMonths?.[0] || '')
            }
        }
    }

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileSpreadsheet className="h-5 w-5" />
                        {t('compare_accounting.title')}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Month selector */}
                    <div className="space-y-1.5">
                        <label className="text-sm font-medium text-gray-700">
                            {t('compare_accounting.report_month')}
                        </label>
                        {defaultMonths && defaultMonths.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                                {defaultMonths.map((month) => (
                                    <button
                                        key={month}
                                        type="button"
                                        onClick={() => setReportMonth(month)}
                                        className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${
                                            reportMonth === month
                                                ? 'bg-blue-600 text-white border-blue-600'
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        {month}
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <Input
                                type="month"
                                value={reportMonth}
                                onChange={(e) => setReportMonth(e.target.value)}
                                className="h-9"
                            />
                        )}
                    </div>

                    {/* File upload */}
                    <div className="space-y-1.5">
                        <label className="text-sm font-medium text-gray-700">
                            {t('compare_accounting.file_label')}
                        </label>
                        <div
                            onClick={() => fileInputRef.current?.click()}
                            className="border-2 border-dashed border-gray-300 rounded-md p-4 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors"
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xlsx"
                                onChange={handleFileChange}
                                className="hidden"
                            />
                            {file ? (
                                <div className="flex items-center justify-center gap-2 text-sm text-green-700">
                                    <FileSpreadsheet className="h-4 w-4" />
                                    {file.name}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-1 text-gray-500">
                                    <Upload className="h-5 w-5" />
                                    <span className="text-xs">
                                        {t('compare_accounting.upload_hint')}
                                    </span>
                                </div>
                            )}
                        </div>
                        <p className="text-xs text-gray-400">
                            {t('compare_accounting.file_format_hint')}
                        </p>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleClose(false)}
                        disabled={isComparing}
                        className="rounded-sm"
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleSubmit}
                        disabled={!reportMonth || !file || isComparing}
                        className="rounded-sm"
                    >
                        {isComparing ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                                {t('compare_accounting.comparing')}
                            </>
                        ) : (
                            t('compare_accounting.submit')
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
